import {
  FlatActionList,
  Text,
  Page,
  Stack,
  Card,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { useGenericFilterListScreenProvider } from "../../../../components/GenericFilterListScreen";
import React from "react";
import {
  ProductListFilterRootParamList,
  ProductRootPartialScreenParamList,
} from "../../../../types";
import { StackScreenProps } from "@react-navigation/stack";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { Q } from "@nozbe/watermelondb";
import Department from "../../../../utilities/data/model/Department";
import { View } from "react-native-ui-lib";
import { Switch } from "react-native";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { ProductsFilterValues } from "../../types";
import Supplier from "../../../../utilities/data/model/Supplier";

export type ProductListFilterScreenProps = StackScreenProps<
  ProductRootPartialScreenParamList & ProductListFilterRootParamList,
  "ProductListFilterScreen"
>;

export const ProductListFilterScreen = ({
  navigation,
}: ProductListFilterScreenProps) => {
  const db = useDatabase();
  const { t } = useTranslation("product");
  const { setScreenOptions } = useGenericFilterListScreenProvider();
  const { watch, setFilterValue } =
    useListFilterContext<ProductsFilterValues>();

  const {
    departmentIds = [],
    categoryIds = [],
    supplierIds = [],
    notRetailerProduct = false,
    isMainProduct = false,
  } = watch();

  const handleOnDepartmentFilterSelect = React.useCallback(async () => {
    const departments = await db.collections
      .get<Department>("departments")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_department.label"),
      options: departments.map((department) => ({
        id: department.id,
        name: department.name,
      })),
      initialSelectedOptions: departmentIds,
      onSelectOption: (ids) => setFilterValue("departmentIds", ids),
    });
    navigation.navigate("GenericFilterListScreen");
  }, [departmentIds]);

  const handleOnCategoryFilterSelect = React.useCallback(async () => {
    const categories = await db.collections
      .get<Department>("categories")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_category.label"),
      options: categories.map((category) => ({
        id: category.id,
        name: category.name,
      })),
      initialSelectedOptions: categoryIds,
      onSelectOption: (ids) => setFilterValue("categoryIds", ids),
    });
    navigation.navigate("GenericFilterListScreen");
  }, [categoryIds]);

  const handleOnSupplierFilterSelect = React.useCallback(async () => {
    const suppliers = await db.collections
      .get<Supplier>("suppliers")
      .query(Q.and(Q.where("deleted_at", Q.eq(null))));

    setScreenOptions({
      title: t("action.filter_supplier.label"),
      options: suppliers.map((supplier) => ({
        id: supplier.id,
        name: supplier.name,
      })),
      initialSelectedOptions: supplierIds,
      onSelectOption: (ids) => setFilterValue("supplierIds", ids),
    });
    navigation.navigate("GenericFilterListScreen");
  }, [supplierIds]);

  return (
    <Page
      title={t("select_product_filter_title")}
      secondaryTitle={t("select_product_filter_subtitle")}
    >
      <Stack vertical>
        <Card>
          <FlatActionList
            items={[
              {
                key: "departments",
                title: t("action.filter_department.label"),
                subTitle: t("action.filter_department.description"),
                isAction: departmentIds.length > 0,
                onPress: handleOnDepartmentFilterSelect,
                componentRight: (icon) => {
                  return departmentIds.length > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">
                        {departmentIds.length} selected
                      </Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
              {
                key: "categories",
                title: t("action.filter_category.label"),
                isAction: categoryIds.length > 0,
                subTitle: t("action.filter_category.description"),
                onPress: handleOnCategoryFilterSelect,
                componentRight: (icon) => {
                  return categoryIds.length > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">{categoryIds.length} selected</Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },

              {
                key: "suppliers",
                title: t("action.filter_supplier.label"),
                subTitle: t("action.filter_supplier.description"),
                isAction: supplierIds.length > 0,
                onPress: handleOnSupplierFilterSelect,
                componentRight: (icon) => {
                  return supplierIds.length > 0 ? (
                    <Stack centerVertical>
                      <Text color="action">{supplierIds.length} selected</Text>
                      {icon}
                    </Stack>
                  ) : (
                    icon
                  );
                },
              },
            ]}
          />
        </Card>
        <Card>
          <FlatActionList
            items={[
              {
                key: "notRetailerProduct",
                title: t("action.filter_not_retailer_product.label"),
                subTitle: t("action.filter_not_retailer_product.description"),
                isAction: notRetailerProduct,
                componentRight: (icon) => {
                  return (
                    <View>
                      <Switch
                        value={notRetailerProduct}
                        onValueChange={() => {
                          setFilterValue(
                            "notRetailerProduct",
                            !notRetailerProduct
                          );
                        }}
                      />
                    </View>
                  );
                },
              },
              {
                key: "isMainProduct",
                title: t("action.filter_is_main_product.label"),
                subTitle: t("action.filter_is_main_product.description"),
                isAction: isMainProduct,
                componentRight: (icon) => {
                  return (
                    <View>
                      <Switch
                        value={isMainProduct}
                        onValueChange={() => {
                          setFilterValue("isMainProduct", !isMainProduct);
                        }}
                      />
                    </View>
                  );
                },
              },
            ]}
          />
        </Card>
      </Stack>
    </Page>
  );
};
